import { motion } from 'framer-motion';
import { Award, Users, BookOpen, TrendingUp } from 'lucide-react';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 },
  };

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-12 md:py-12 mesh-gradient relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-black/5 grid-pattern"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div {...fadeInUp} className="max-w-4xl">
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-950 mb-6 tracking-tight">
              About <span className="text-[#B6671D]">IRASM-2027</span>
            </h1>
            <p className="font-inter text-lg text-slate-600 leading-relaxed">
              Creating a global platform for innovation and collaboration in robotics, automation, and smart manufacturing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Conference */}
      <section className="py-20 bg-white" data-testid="about-conference">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp}>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-950 mb-6">
              About the Conference
            </h2>
            <div className="space-y-6 font-inter text-slate-600 leading-relaxed">
              <p>
                The International Conference on Robotics, Automation and Smart Manufacturing (IRASM-2027) aims to create
                a global platform for researchers, academicians, industry experts, and policymakers to discuss advancements
                in robotics, automation, artificial intelligence, smart manufacturing, aerospace systems, electric mobility,
                and drone technologies.
              </p>
              <p>
                The conference promotes interdisciplinary collaboration and sustainable technological innovations. It brings
                together leading minds from academia and industry to share research findings, explore emerging trends, and
                foster partnerships that drive technological advancement.
              </p>
              <div className="bg-[#B6671D]/10 border-l-4 border-[#B6671D] p-6 rounded-r-lg">
                <p className="font-semibold text-slate-900 mb-2">Publication Opportunity</p>
                <p>
                  Accepted and presented papers will be submitted for publication in international conference proceedings
                  indexed in <strong>Web of Science, Scopus, and Inspec</strong>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Conference Objectives */}
      <section className="py-20 bg-slate-50" data-testid="conference-objectives">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-950 mb-6">
              Conference Objectives
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              'Promote advanced research in intelligent robotics',
              'Advance smart manufacturing and Industry 5.0',
              'Enable sustainable and green technologies',
              'Strengthen industry–academia collaboration',
              'Encourage interdisciplinary innovation',
              'Provide global networking opportunities',
              'Empower young researchers and students',
              'Enhance institutional and global visibility',
            ].map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-start space-x-4 bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                data-testid={`objective-${index}`}
              >
                <div className="w-2 h-2 bg-[#B6671D] rounded-full mt-2 flex-shrink-0"></div>
                <p className="font-inter text-slate-700">{objective}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Manipal University Jaipur */}
      <section className="py-20 bg-white" data-testid="about-university">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-950 mb-6">
                About Manipal University Jaipur
              </h2>
              <div className="space-y-4 font-inter text-slate-600 leading-relaxed">
                <p>
                  Manipal University Jaipur (MUJ), established in 2011, is a multidisciplinary private university located
                  in Jaipur, Rajasthan. Spread across a 122-acre campus at Dehmi Kalan, the university offers undergraduate,
                  postgraduate, and doctoral programs across engineering, management, law, design, sciences, and humanities.
                </p>
                <p>
                  MUJ is committed to providing world-class education with state-of-the-art infrastructure, experienced
                  faculty, and strong industry connections. The university fosters innovation, research, and holistic
                  development of its students.
                </p>
              </div>
            </motion.div>

            <motion.div
              {...fadeInUp}
              className="bg-slate-50 border border-slate-200 rounded-xl p-8"
            >
              <h3 className="font-outfit text-2xl font-bold text-slate-950 mb-6">Rankings & Accreditations</h3>
              <div className="space-y-4">
                {[
                  { rank: 'NIRF 2025', detail: '58th in Engineering, 98th Overall' },
                  { rank: 'QS Asia Ranking 2025', detail: '701–800 band' },
                  { rank: 'QS Sustainability 2026', detail: '1201–1250' },
                  { rank: 'THE 2025', detail: '1201–1500 globally' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3" data-testid={`ranking-${index}`}>
                    <Award className="text-[#B6671D] flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-semibold text-slate-900">{item.rank}</p>
                      <p className="text-sm text-slate-600">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Department */}
      <section className="py-20 bg-slate-50" data-testid="about-department">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp}>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-950 mb-6">
              Department of Mechatronics Engineering
            </h2>
            <div className="space-y-6 font-inter text-slate-600 leading-relaxed">
              <p>
                The Department of Mechatronics Engineering at Manipal University Jaipur focuses on multidisciplinary
                education and cutting-edge research in robotics, automation, embedded systems, and smart technologies.
                The department is committed to preparing students for the challenges of Industry 4.0 and beyond.
              </p>

              <div className="bg-white border border-slate-200 rounded-xl p-8">
                <h3 className="font-outfit text-2xl font-bold text-slate-950 mb-6">Programs Offered</h3>
                <div className="space-y-4">
                  {[
                    { icon: BookOpen, program: 'B.Tech in Mechatronics Engineering' },
                    { icon: Users, program: 'B.Tech in Robotics and AI' },
                    { icon: TrendingUp, program: 'M.Tech in Industrial Automation and Robotics' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-4" data-testid={`program-${index}`}>
                      <div className="w-12 h-12 bg-[#B6671D]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="text-[#B6671D]" size={24} />
                      </div>
                      <p className="font-medium text-slate-900">{item.program}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;